import React from "react";
import Joi from "joi-browser";
import isEmpty from "lodash/isEmpty";
import queryString from "query-string";

import { DirectUpload } from "activestorage";
import { useJobBoardJob, useApplyForJob } from "@shared/queryHooks/jobBoard/useJobBoard";
import { objectHasEmptyValues, sanitizeTrackingParams } from "@shared/lib/utils";

import ApplicationForm from "./ApplicationForm";
import EmptyState from "@ats/src/components/shared/EmptyState";
import LoadingIndicator from "@ats/src/components/shared/LoadingIndicator";

import styled from "@emotion/styled";
import { css } from "@emotion/react";

/* REDUCER
--===================================================-- */
function questionResponsesReducer(state, action) {
  switch (action.type) {
    case "SET_QUESTION_RESPONSE": {
      return { ...state, [action.payload.questionId]: action.payload };
    }
    case "DELETE_QUESTION_RESPONSE": {
      const newState = { ...state };
      delete newState[action.payload.questionId];
      return newState;
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

function ApplyApp(props) {
  const [submitted, setSubmitted] = React.useState(false);
  const [uploadingFile, setUploadingFile] = React.useState(false);
  const [errors, setErrors] = React.useState(null);
  const [questionResponsesState, questionResponsesDispatch] = React.useReducer(
    questionResponsesReducer,
    {},
  );

  const { src, source } = queryString.parse(location.search);
  const { utmSource, utmCampaign } = sanitizeTrackingParams(location.search);
  const referrer = document.referrer;
  const recaptchaRef = React.useRef(null);

  // window.logger("%c[ApplyApp] RENDER", "color: #1976D2", { props, location, src });

  const [state, setState] = React.useState({
    name: "",
    email: "",
    phone: "",
    location: "",
    photo: "",
    coverLetter: "",
    linkedinUrl: "",
    twitterUrl: "",
    githubUrl: "",
    dribbbleUrl: "",
    websiteUrl: "",
    success: false,
    errors: null,
    resumeFile: undefined, // needs to be undefined for Joi
  });

  const jobId = window.inflowJobId;
  const organizationSlug = window.inflowOrganizationSlug;

  const { data: job, isLoading } = useJobBoardJob({
    jobId,
    organizationSlug,
  });

  const { mutate: applyForJob, isLoading: isLoadingApplyForJob } = useApplyForJob();

  if (isLoading === true) {
    return (
      <Styled.Loader>
        <LoadingIndicator label={window.APP_TRANSLATIONS.loading_form} />
      </Styled.Loader>
    );
  }

  const handleInputChange = ({ errors, ...inputValue }) => {
    setState({ ...state, ...inputValue });
    setErrors(errors);
  };

  const handleQuestionResponseChange = (questionIndex, response, errors) => {
    // window.logger("%c[ApplyApp] handleQuestionResponseChange", "color: #1976D2", { response });

    if (errors != undefined && errors.customQuestionsErrors != undefined) {
      delete errors.customQuestionsErrors;
    }

    // setState({ ...state, errors: isEmpty(errors) ? undefined : errors });
    setErrors(isEmpty(errors) ? undefined : errors);
    //check for responses updated with null/empty/undefined body/array/file
    const invalidResponse = objectHasEmptyValues(response);

    questionResponsesDispatch({
      type: "SET_QUESTION_RESPONSE",
      payload: response,
    });
    //if invalid, update question state (above) so UI updates correctly
    //then delete invalid response to ensure required questions error
    if (invalidResponse) {
      questionResponsesDispatch({
        type: "DELETE_QUESTION_RESPONSE",
        payload: response,
      });
    }
  };

  /* onSubmit
  --===================================================-- */
  const onSubmit = async () => {
    const recaptchaToken = window.IS_TEST_ENV ? null : await recaptchaRef.current.executeAsync();

    const {
      name,
      email,
      phone,
      location,
      coverLetter,
      linkedinUrl,
      githubUrl,
      twitterUrl,
      dribbbleUrl,
      websiteUrl,
      resumeFile,
    } = state;

    if (isLoadingApplyForJob) {
      return;
    }

    let joiObject = {
      name: Joi.string().required(),
      email: Joi.string().email().required(),
      resumeFile: Joi.any().required(),
    };

    // Trim email and update state
    const trimmedEmail = email.trim();
    setState({ ...state, email: trimmedEmail });

    let objectToValidate = { name, email:trimmedEmail, resumeFile };

    if (job.settings["resume"] !== "required") {
      delete joiObject.resumeFile;
      delete objectToValidate.resumeFile;
    }

    // window.logger("%c[ApplyApp] onSubmit", "color: #1976D2", {
    //   questionResponses: Object.values(questionResponsesState),
    //   resumeFile,
    //   settings: job.settings,
    //   objectToValidate,
    //   joiObject,
    //   recaptchaToken,
    // });

    const schema = Joi.object().keys(joiObject);

    Joi.validate(objectToValidate, schema, { abortEarly: false }, (err, value) => {
      let errors;
      let questionsWithErrors = job.questions.filter((question, index) => {
        // console.log("%c[ApplyApp] questionsWithErrors", "color: #1976D2", { question });
        return (
          question.requirementSetting === "required" &&
          (questionResponsesState[question.id] == undefined ||
            isEmpty(questionResponsesState[question.id]))
          // (question.questionResponse == undefined || isEmpty(question.questionResponse.body))
        );
      });

      if (err != undefined) {
        errors = {};
        console.error("[ApplyApp] onSubmit err.details", err.details);

        err.details.forEach((index) => {
          let key = index.context.key;
          let message = index.message.replace(`"${key}" `, "");
          errors[key] = [message];
        });
      } else if (questionsWithErrors.length > 0) {
        errors = { customQuestionsErrors: {} };

        questionsWithErrors.forEach((question) => {
          errors.customQuestionsErrors[question.id] = ["is required"];
        });
      } else {
        handleSubmission({
          candidate: {
            name,
            email: trimmedEmail,
            phone,
            location,
            coverLetter,
            linkedinUrl,
            githubUrl,
            twitterUrl,
            dribbbleUrl,
            websiteUrl,
          },
          resumeFile,
          questionResponsesState,
          createdVia: "created_via_job_board",
          src,
          source, // pulled from the URL ?src=param
          referrer,
          utmSource,
          utmCampaign,
          recaptchaToken,
        });
      }
      // setState({ ...state, errors });
      setErrors(errors);
      recaptchaRef?.current?.reset();
    });
  };

  const handleSubmission = async ({
    candidate,
    resumeFile,
    questionResponsesState,
    createdVia,
    src,
    source,
    referrer,
    utmSource,
    utmCampaign,
    recaptchaToken,
  }) => {
    const fileUploadQuestions = job.questions.filter((question) => question.kind === "file_upload");
    let responses;
    if (!isEmpty(fileUploadQuestions)) {
      responses = await handleCustomFileUploads(fileUploadQuestions, questionResponsesState);
    } else {
      responses = Object.values(questionResponsesState);
    }

    if (resumeFile) {
      // Upload Resume, then postRequest with returned resumeSignedId
      const resumeSignedId = await directUploadFile(resumeFile);
      createCandidate({
        candidate: { ...candidate, resumeSignedId: resumeSignedId },
        questionResponses: responses,
        createdVia,
        src,
        source,
        referrer,
        utmSource,
        utmCampaign,
        recaptchaToken,
      });
    } else {
      createCandidate({
        candidate,
        questionResponses: responses,
        createdVia,
        src,
        source,
        referrer,
        utmSource,
        utmCampaign,
        recaptchaToken,
      });
    }
  };

  const createCandidate = ({
    candidate,
    questionResponses,
    createdVia,
    src,
    source,
    referrer,
    utmSource,
    utmCampaign,
    recaptchaToken,
  }) => {
    // window.logger("%c[ApplyApp] createCandidate ", "color: #1976D2", {
    //   jobId,
    //   organizationSlug,
    //   application: {
    //     candidate,
    //     questionResponses,
    //   },
    // });

    applyForJob(
      {
        jobId,
        organizationSlug,
        application: {
          candidate,
          questionResponses,
          createdVia,
          src,
          source,
          referrer,
          utmSource,
          utmCampaign,
        },
        recaptchaToken,
      },
      {
        onSuccess: (data) => {
          setSubmitted(true);
          setUploadingFile(false);
        },
        onError: (response) => {
          setErrors((response as any).data.errors);
          setUploadingFile(false);
          recaptchaRef?.current?.reset();
        },
      },
    );
  };

  /* UPLOAD HANDLERS */
  const directUploadFile = async (file) => {
    setUploadingFile(true);
    const railsDirectUploadUrl = `${window.APP_ATS_ROOT_URL}/api/v1/public/rails/active_storage/direct_uploads`;
    const upload = new DirectUpload(file, railsDirectUploadUrl, this);

    return new Promise((resolve, reject) => {
      upload.create((error, blob) => {
        if (error) {
          console.error(`[ApplyApp] directUploadFile error while uploading ${file.name}`, error);
          reject(error);
        } else {
          console.log(`%c[ApplyApp] directUploadFile ${file.name} blob`, "color: #1976D2", blob);
          resolve(blob.signed_id);
        }
      });
    });
  };

  const handleCustomFileUploads = async (fileUploadQuestions, responsesState) => {
    let fileUploads = [];
    fileUploadQuestions.forEach((question) => {
      if (responsesState[question.id] !== undefined && !isEmpty(responsesState[question.id])) {
        fileUploads.push(questionResponsesState[question.id]);
      }
    });
    // iterate through responses with files and upload each
    const fileUploadsResponses = {};
    for (const upload of fileUploads) {
      const signedId = await directUploadFile(upload.customFile);
      // assign returned signedId as the value of customFile
      const newResponse = { questionId: upload.questionId, customFile: signedId };
      fileUploadsResponses[upload.questionId] = newResponse;
    }
    // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/assign#merging_objects_with_same_properties
    const updatedQuestionResponsesState = Object.assign({}, responsesState, fileUploadsResponses);
    return Object.values(updatedQuestionResponsesState);
  };

  /* Handle successful submission */
  if (submitted) {
    return (
      <EmptyState
        roomy
        icon="send"
        title={window.APP_TRANSLATIONS.application_success_title}
        message={window.APP_TRANSLATIONS.application_success_body}
      />
    );
  }

  return (
    <>
      <Styled.Container>
        <ApplicationForm
          {...state}
          {...props}
          {...job}
          errors={errors}
          submitLoading={uploadingFile || isLoadingApplyForJob}
          onSubmit={onSubmit} //has to be after {...props} to override the onSubmit passed down
          onInputChange={handleInputChange}
          questionResponsesState={questionResponsesState}
          handleQuestionResponseChange={handleQuestionResponseChange}
          recaptchaRef={recaptchaRef}
        />
      </Styled.Container>
      <Styled.Recaptcha>
        This site is protected by reCAPTCHA and the Google{" "}
        <a href="https://policies.google.com/privacy">Privacy Policy</a> and{" "}
        <a href="https://policies.google.com/terms">Terms of Service</a> apply.
      </Styled.Recaptcha>
    </>
  );
}

export default ApplyApp;

/* Styled Components
======================================================= */
const Styled: any = {};

Styled.Container = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: ApplyApp;
    ${[t.text.sm, t.p(4), t.rounded.md]}
    border: 1px solid ${t.color.gray[200]};
    background: ${t.color.white};
    width: 100%;

    ${t.mq["lg"]} {
      ${[t.p(6), t.pb(8)]}
    }
  `;
});

Styled.Loader = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: ApplyApp_Loader;
    ${t.rounded.md}
    border: 1px solid ${t.color.gray[200]};
    background: ${t.color.white};
    width: 100%;
  `;
});

Styled.Recaptcha = styled.p((props: any) => {
  const t: any = props.theme;
  return css`
    label: ApplyApp_Recaptcha;
    ${[t.text.xs, t.my(4)]}
    color: ${t.color.gray[600]};
    text-align: center;
    a:hover {
      color: ${t.color.black};
    }

    ${t.mq["lg"]} {
      text-align: left;
    }
  `;
});
